import { ChevronRight } from "lucide-react";
import { Link } from "react-router-dom";

import { useFeedDirectory, useNowPlaying } from "../../api/hooks.js";
import type { FeedSummary } from "../../api/types.js";
import { cn } from "../../design-system/cn.js";
import { FeedStatePill } from "../../design-system/feed-state.js";
import { StateSurface } from "../../design-system/surfaces.js";
import { Artwork, LiveBars } from "../../player/artwork.js";
import { useFeedPlayback, usePlayer } from "../../player/player-provider.js";
import { AiredProgress, PlayButton } from "../../player/transport.js";
import { useNow } from "../../lib/use-now.js";
import { PageHeading } from "../shared/page-heading.js";
import { RecentlyPlayed } from "./recently-played.js";

/**
 * Every feed the station publishes, each one showing what is actually on it right
 * now. Feeds are plural here by construction — nothing on this page is written in
 * the singular, and one feed renders as one row of the same list that ten feeds
 * render as.
 */
export function FeedsPage() {
  const directory = useFeedDirectory();
  const { target } = usePlayer();
  const feeds = directory.data?.feeds ?? [];

  // Whose history to show under the list: whichever feed is being listened to, or
  // the only one there is. With several feeds and none playing, picking one would be
  // arbitrary, so nothing is shown rather than something guessed.
  const focused =
    feeds.find((feed) => feed.id === target?.feedId) ??
    (feeds.length === 1 ? feeds[0] : undefined);

  return (
    <div className="flex flex-col gap-8">
      <PageHeading
        subtitle="What the station is publishing, and what is on each feed right now."
        title="Feeds"
      />

      {directory.loading ? (
        <StateSurface kind="loading" title="Looking for feeds" />
      ) : feeds.length > 0 ? (
        <ul className="flex flex-col gap-2.5">
          {feeds.map((feed) => (
            <li key={feed.id}>
              <FeedRow feed={feed} />
            </li>
          ))}
        </ul>
      ) : (
        <StateSurface kind="empty" title="No feeds to listen to">
          The station has not published any feeds yet.
        </StateSurface>
      )}

      {focused ? (
        <RecentlyPlayed
          feedId={focused.id}
          heading={`Recently on ${focused.name}`}
        />
      ) : null}
    </div>
  );
}

function FeedRow({ feed }: { feed: FeedSummary }) {
  const live = useNowPlaying(feed.id);
  const { play, stop } = usePlayer();
  const { isCurrent, state } = useFeedPlayback(feed.id);
  const now = useNow(1000);
  const airing = live.data?.nowPlaying ?? null;
  const streamUrl = live.data?.streamUrl ?? null;
  const playing = isCurrent && state === "playing";

  return (
    <Link
      className={cn(
        "group flex items-center gap-4 rounded-md border bg-surface p-3 transition-colors sm:p-4",
        playing
          ? "border-accent-line bg-[color-mix(in_oklch,var(--accent)_6%,var(--surface))]"
          : "border-border hover:border-border-hi hover:bg-surface-hi",
      )}
      to={`/feeds/${feed.id}`}
    >
      <Artwork airing={airing} className="size-14 sm:size-[4.5rem]" />

      <div className="min-w-0 flex-1">
        <div className="flex items-center gap-2">
          {/* The docked player already carries this on a phone, where the name is
              what the row is for and every pixel of it counts. */}
          {playing ? (
            <LiveBars className="hidden h-2.5 text-accent-text sm:flex" />
          ) : null}
          <span className="truncate text-sm font-semibold text-ink">
            {feed.name}
          </span>
          <FeedStatePill
            className="shrink-0"
            state={live.data?.state ?? null}
          />
        </div>
        <div className="mt-1.5 truncate text-sm text-ink-2">
          {live.loading
            ? "…"
            : (airing?.title ??
              (live.error ? "Not reachable" : "Nothing airing"))}
        </div>
        {airing?.artist ? (
          <div className="mt-0.5 truncate text-xs text-ink-3">
            {airing.artist}
          </div>
        ) : feed.description ? (
          <div className="mt-0.5 truncate text-xs text-ink-3">
            {feed.description}
          </div>
        ) : null}
        {airing ? (
          <AiredProgress
            className="mt-2.5 max-w-xs"
            now={now}
            snapshot={live.data}
          />
        ) : null}
      </div>

      <PlayButton
        disabled={!streamUrl}
        feedName={feed.name}
        onActivate={() => {
          if (!streamUrl) return;
          if (isCurrent && state !== "idle" && state !== "error") stop();
          else play({ feedId: feed.id, feedName: feed.name, streamUrl });
        }}
        state={state}
      />
      <ChevronRight
        aria-hidden="true"
        className="hidden size-4 shrink-0 text-ink-3 transition-colors group-hover:text-ink-2 sm:block"
      />
    </Link>
  );
}
